import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
    CMPS_DIALOG_DATA,
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { skip, tap } from 'rxjs/operators';

import { FocusDto } from '../../coaching-base/general';
import {
    CoachingSwitchboardService,
    SwitchboardObservationReviewComm,
} from '../../coaching-base/switchboard';
import { ObservationSummaryContainerView } from '../../coaching-detail';
import { FocusShallow } from '../../coaching-tags/tags/tags.model';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { CoachingObservationDialogService } from '../coaching-observation-dialog.service';
import { TranslationKeyMap } from '../coaching-observation.i18n';
import { ObservationControllerMode } from '../observation-controller/controller.observation.model';
import { ObservationComponent } from '../observation/observation.component';
import {
    AllowedActions,
    EnabledActions,
    ObservationMode,
    READONLY_OBSERVATION,
    ReadonlyObservation,
    ReadonlyObservationContainer,
} from '../observation/observation.model';

import {
    goToObservation,
    nextObservation,
    prevObservation,
    reload,
    setInReview,
} from './observation-review.action';
import observationReviewReducer from './observation-review.reducer';
import { ObservationReviewState } from './observation-review.state';

interface ObservationReviewOptions {
    observationId: number;
    inReview: boolean;
    isLinkMode: boolean;
    focus: FocusDto; // won't support value changes for this after ngOnInit
}

export interface ObservationReviewDialogData {
    options: ObservationReviewOptions;
    observationIdChange: (nextId: number) => void;
}

@Component({
    selector: '[cmpsObservationReview]',
    templateUrl: './observation-review.component.html',
    host: {
        class: 'cmps-observation-review',
        '[class.cmps-observation-review--expanded]': '_dialogRef?.expanded',
        'data-e2e': 'component-observation-review',
    },
    viewProviders: [
        {
            provide: READONLY_OBSERVATION,
            useValue: ReadonlyObservationContainer,
        },
    ],
})
@TakeUntilDestroy()
@UseStore<ObservationReviewState>(
    'observationReview.root',
    observationReviewReducer.observationReview.root,
)
export class ObservationReviewComponent
    implements
        OnInit,
        OnDestroy,
        StateToProps<ObservationReviewState>,
        SwitchboardObservationReviewComm
{
    state: ObservationReviewState;

    /** @private */ @ViewChild(ObservationComponent)
    _observation: ObservationComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _ObservationControllerMode = ObservationControllerMode;
    /** @private */ _ObservationMode = ObservationMode;
    /** @private */ _allowedActions: AllowedActions;
    /** @private */ _enabledActions: EnabledActions;

    private _observationId: number;
    private _inReview: boolean;
    /** @private */ _focus: FocusDto;
    private _isLinkMode: boolean;

    // eslint-disable-next-line accessor-pairs
    /** @input */ set options(value: ObservationReviewOptions) {
        const cObservationId = this._observationId !== value.observationId;
        const cInReview = this._inReview !== value.inReview;

        this._observationId = value.observationId;
        this._inReview = value.inReview;
        this._focus = value.focus;
        this._isLinkMode = value.isLinkMode;

        // don't do anything until after ngOnInit
        if (!this.state) {
            return;
        }

        cInReview &&
            useDispatcher(this, setInReview({ inReview: this._inReview })); // must be dispatched first
        cObservationId &&
            useDispatcher(
                this,
                goToObservation({ observationId: this._observationId }),
            );
    }

    constructor(
        private readonly _translateService: TranslateService,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _observationDialogService: CoachingObservationDialogService,
        private readonly _dialog: CmpsDialog,
        /** @private */ readonly _dialogRef: CmpsDialogRef<
            ObservationReviewComponent,
            any
        >,
        @Inject(CMPS_DIALOG_DATA)
        private readonly _options: ObservationReviewDialogData,
        @Inject(READONLY_OBSERVATION)
        private readonly _readonly: ReadonlyObservation,
    ) {
        this._inReview = true;
        this.options = _options.options;
    }

    ngOnInit(): void {
        this._reload(
            this._switchboardService.pillarTwo.sbOrderedObservationsSnapshot(),
            this._focus,
        );
        const obs = this._switchboardService.pillarTwo.sbOrderedObservations();
        if (obs) {
            obs.pipe(
                skip(1),
                tap(() => {
                    this._reload(
                        this._switchboardService.pillarTwo.sbOrderedObservationsSnapshot(),
                        this._focus,
                    );
                }),
                takeUntilDestroy(this),
            ).subscribe();
        }

        this._switchboardService.observationReview.connect(this);
    }

    ngOnDestroy(): void {
        this._switchboardService.observationReview.disconnect();

        useDispatcher(this, setInReview({ inReview: true }));
        // since this component is being destroyed ensure readonly is set as stateToProps may not be called
        this._readonly.value = true;
    }

    stateToProps(prevState?: ObservationReviewState): void {
        if (prevState.selectedObservation !== this.state.selectedObservation) {
            this._observationId = this.state.selectedObservation
                ? this.state.selectedObservation.observationId
                : null;
            this._inReview = this.state.inReview;
            this._focus = this.state.focus;
        }

        // always emits
        this.state.selectedObservation &&
            this._options.observationIdChange &&
            this._options.observationIdChange(
                this.state.selectedObservation.observationId,
            );

        if (prevState.inReview !== this.state.inReview) {
            this._readonly.value = this.state.inReview;

            if (!this.state.inReview) {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._observation && this._observation.cancel();
            }
        }

        if (
            (this._isLinkMode && !this.state.observations) ||
            this.state.observations.length === 0
        ) {
            const data: CmpsConfirmDialogOptions = {
                title: this._translateService.instant(
                    TranslationKeyMap._9479_CONFIRMATION,
                ),
                message: this._translateService.instant(
                    TranslationKeyMap._9726_THERE_IS_NO_MATCHING_FOCUS_TO_LINK__DO_YOU_WANT_TO_START_A_NEW_OBSERVATION_,
                ),
                actions: [
                    {
                        label: this._translateService.instant(
                            TranslationKeyMap._9297_YES,
                        ),
                        data: 0,
                    },
                    {
                        label: this._translateService.instant(
                            TranslationKeyMap._9298_NO,
                        ),
                        data: 1,
                    },
                ],
            };
            const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
            ref.result
                .pipe(
                    tap((ret) => {
                        if (ret !== 0) {
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            this._dialogRef && this._dialogRef.close();
                            return;
                        }

                        this._observationDialogService.openNew({
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                            focus: {
                                behaviourId: this._focus.behaviourId,
                                subBehaviourId: this._focus.subBehaviourId,
                            } as FocusShallow,
                        });
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();
        }
    }

    sbCancelEdit(): void {
        useDispatcher(this, setInReview({ inReview: true }));
    }

    sbWillNavigate(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._dialogRef && this._dialogRef.close();
    }

    /** @private */ _onCancel(): void {
        this._canGo(() => {
            useDispatcher(this, setInReview({ inReview: true }));
        });
    }

    /** @private */ _onEdit(): void {
        this._canGo(() => {
            useDispatcher(this, setInReview({ inReview: false }));
        });
    }

    /** @private */ _onPrevious(): void {
        this._canGo(() => {
            useDispatcher(this, prevObservation({}));
        });
    }

    /** @private */ _onNext(): void {
        this._canGo(() => {
            useDispatcher(this, nextObservation({}));
        });
    }

    private _reload(
        observations: ObservationSummaryContainerView[],
        focus: FocusDto,
    ): void {
        useDispatcher(
            this,
            reload({
                observations,
                observationId: this._observationId,
                inReview: this._inReview,
                focus,
                force: true,
            }),
        );
    }

    private _canGo(go: () => void): void {
        if (this._readonly.value) {
            go();
        } else {
            const ret = this._switchboardService.observation.sbCanCancelEdit();
            if (ret instanceof Observable) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                ret.pipe(
                    tap((canGo) => {
                        if (canGo) {
                            this._switchboardService.observation.sbCancelEdit();
                            go();
                        }
                    }),
                ).toPromise();
            } else {
                if (ret) {
                    go();
                }
            }
        }
    }
}
